import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const MAX_IMAGES = 4;
const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

const UpdateForm = ({ product, onClose }) => {
  // --- Static Data ---
  const categories = ['Shop Category', 'Indoor', 'Outdoor', 'Wedding', 'Gym', 'Office', 'Meeting', 'School', 'College'];
  const perfumeVolumes = ['30ml', '50ml', '100ml'];
  const attarVolumes = ['3ml', '6ml', '12ml'];
  const stockOptions = ['In stock', 'Out of stock'];
  const productTypes = ['Normal', 'New Arrival'];
  const offerTypes = ['None', 'Discount', 'Offer'];
  const fragranceTypes = ['Perfumes', 'Attars'];

  // Get volumes based on fragrance type
  const getVolumesForType = (fragranceType) => {
    return fragranceType === 'Attars' ? attarVolumes : perfumeVolumes;
  };

  // Initialize prices based on existing product data
  const initializePrices = (product) => {
    const volumes = getVolumesForType(product?.fragranceType);
    const prices = {};
    const discountedPrices = {};

    volumes.forEach(volume => {
      if (product?.prices && product.prices[volume]) {
        prices[volume] = typeof product.prices[volume] === 'object' 
          ? product.prices[volume].originalPrice || '' 
          : product.prices[volume] || '';
        discountedPrices[volume] = typeof product.prices[volume] === 'object' 
          ? product.prices[volume].discountedPrice || '' 
          : '';
      } else {
        prices[volume] = '';
        discountedPrices[volume] = '';
      }
    });

    return { prices, discountedPrices };
  };

  const initialPrices = initializePrices(product);

  // --- State ---
  const [formData, setFormData] = useState({
    name: product?.name || '',
    prices: initialPrices.prices,
    discountedPrices: initialPrices.discountedPrices,
    category: product?.category || '',
    fragranceType: product?.fragranceType || '',
    fragranceNotes: product?.fragranceNotes || [],
    currentNote: '',
    stockAvailability: product?.stockAvailability || 'In stock',
    description: product?.description || '',
    productType: product?.isNewArrival ? 'New Arrival' : 'Normal',
    offerType: product?.prices && Object.values(product.prices).some(p => typeof p === 'object' && p.discountedPrice) ? 'Discount' : 'None',
  });

  const [offerPercentage, setOfferPercentage] = useState('');

  // Image handling state
  const [existingImageUrls, setExistingImageUrls] = useState(product?.images || []);
  const [newImages, setNewImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // --- Derived State ---
  const currentVisibleExistingImages = existingImageUrls.filter(url => !imagesToRemove.includes(url));
  const totalImageCount = currentVisibleExistingImages.length + newImages.length;
  const canUploadMore = totalImageCount < MAX_IMAGES;
  const showDiscountFields = formData.offerType === 'Discount' || formData.offerType === 'Offer';
  const showOfferPercentage = formData.offerType === 'Offer';
  const volumes = getVolumesForType(formData.fragranceType);

  // --- Reset prices when fragrance type changes ---
  useEffect(() => {
    if (formData.fragranceType && product?.fragranceType !== formData.fragranceType) {
      const newVolumes = getVolumesForType(formData.fragranceType);
      const newPrices = {};
      const newDiscountedPrices = {};
      
      newVolumes.forEach(volume => {
        newPrices[volume] = '';
        newDiscountedPrices[volume] = '';
      });
      
      setFormData(prev => ({
        ...prev,
        prices: newPrices,
        discountedPrices: newDiscountedPrices
      }));
      setOfferPercentage('');
    }
  }, [formData.fragranceType, product?.fragranceType]);

  // --- Auto-calculate offer prices when percentage changes ---
  useEffect(() => {
    if (formData.offerType === 'Offer' && offerPercentage && offerPercentage > 0) {
      const newDiscountedPrices = {};
      volumes.forEach(volume => {
        const originalPrice = parseFloat(formData.prices[volume]);
        if (originalPrice && !isNaN(originalPrice)) {
          const discountAmount = (originalPrice * parseFloat(offerPercentage)) / 100;
          const discountedPrice = originalPrice - discountAmount;
          newDiscountedPrices[volume] = discountedPrice.toFixed(2);
        } else {
          newDiscountedPrices[volume] = '';
        }
      });
      
      setFormData(prev => ({
        ...prev,
        discountedPrices: newDiscountedPrices
      }));
    }
  }, [offerPercentage, formData.prices, formData.offerType, volumes]);

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear offer percentage when switching away from 'Offer'
    if (name === 'offerType' && value !== 'Offer') {
      setOfferPercentage('');
    }
  };

  const handleOfferPercentageChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
      setOfferPercentage(value);
    }
  };

  const handlePriceChange = (volume, value) => {
    setFormData((prev) => ({
      ...prev,
      prices: { ...prev.prices, [volume]: value },
    }));
  };

  const handleDiscountedPriceChange = (volume, value) => {
    setFormData((prev) => ({
      ...prev,
      discountedPrices: { ...prev.discountedPrices, [volume]: value },
    }));
  };

  const handleAddNote = () => {
    if (formData.currentNote.trim()) {
      setFormData((prev) => ({
        ...prev,
        fragranceNotes: [...prev.fragranceNotes, formData.currentNote.trim()],
        currentNote: '',
      }));
    }
  };

  const handleRemoveNote = (index) => {
    setFormData((prev) => ({
      ...prev,
      fragranceNotes: prev.fragranceNotes.filter((_, i) => i !== index),
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const availableSlots = MAX_IMAGES - totalImageCount;

    if (files.length > availableSlots) {
      toast.error(`You can only upload ${availableSlots} more image(s). Max ${MAX_IMAGES} total.`, {
        position: "top-center",
        autoClose: 3000,
      });
      const filesToAdd = files.slice(0, availableSlots);
      setNewImages((prev) => [...prev, ...filesToAdd]);
    } else {
      setNewImages((prev) => [...prev, ...files]);
    }
    e.target.value = null;
  };

  const removeNewImage = useCallback((index) => {
    const imageToRemove = newImages[index];
    if (imageToRemove instanceof File) {
      URL.revokeObjectURL(URL.createObjectURL(imageToRemove));
    }
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  }, [newImages]);

  const markExistingImageForRemoval = useCallback((url) => {
    setImagesToRemove((prev) => [...prev, url]);
  }, []);

  const unmarkExistingImageForRemoval = useCallback((url) => {
    setImagesToRemove((prev) => prev.filter(removeUrl => removeUrl !== url));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);

    const formDataToSend = new FormData();

    // Prepare product payload
    const productPayload = {
      ...formData,
      currentNote: undefined,
      isNewArrival: formData.productType === 'New Arrival',
      discountedPrices: (formData.offerType === 'Discount' || formData.offerType === 'Offer') ? formData.discountedPrices : undefined,
    };

    // Append product data as JSON string
    formDataToSend.append('product', JSON.stringify(productPayload));

    // Append new images
    newImages.forEach((file, index) => {
      formDataToSend.append(`image${index + 1}`, file);
    });

    // Append images to remove
    formDataToSend.append('remove_images', JSON.stringify(imagesToRemove));

    try {
      const response = await axios.put(
        `${API_BASE_URL}/update-product/${product.id}`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Success toast
      toast.success('Product updated successfully!', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });

      // Close the form after successful update
      setTimeout(() => {
        onClose(response.data); // Pass updated product data back
      }, 1500);
    } catch (error) {
      console.error('Error updating product:', error);

      // Error toast with detailed message if available
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to update product. Please try again.';

      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Cleanup effect for object URLs
  useEffect(() => {
    return () => {
      newImages.forEach(file => {
        if (file instanceof File) {
          URL.revokeObjectURL(URL.createObjectURL(file));
        }
      });
    };
  }, [newImages]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8 px-4">
      <ToastContainer />
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 p-4 sm:p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Update Product</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              disabled={isLoading}
              aria-label="Close form"
            >
              <FiX size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="overflow-y-auto flex-grow">
          <form id="update-form-main" onSubmit={handleSubmit} className="p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Product Name */}
              <div className="col-span-full">
                <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input
                  id="productName"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                  placeholder="Enter product name"
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Fragrance Type */}
              <div className="col-span-full">
                <label htmlFor="fragranceType" className="block text-sm font-medium text-gray-700 mb-1">
                  Fragrance Type *
                </label>
                <select 
                  id="fragranceType" 
                  name="fragranceType" 
                  value={formData.fragranceType} 
                  onChange={handleChange} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white" 
                  required 
                  disabled={isLoading}
                >
                  <option value="">Select Fragrance Type</option>
                  {fragranceTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Product Type */}
              <div className="col-span-full md:col-span-1">
                <label htmlFor="productType" className="block text-sm font-medium text-gray-700 mb-1">
                  Product Type *
                </label>
                <select
                  id="productType"
                  name="productType"
                  value={formData.productType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white"
                  required
                  disabled={isLoading}
                >
                  {productTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Offer Type */}
              <div className="col-span-full md:col-span-1">
                <label htmlFor="offerType" className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Type *
                </label>
                <select
                  id="offerType"
                  name="offerType"
                  value={formData.offerType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white"
                  required
                  disabled={isLoading}
                >
                  {offerTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Offer Percentage (conditionally rendered) */}
              {showOfferPercentage && (
                <div className="col-span-full">
                  <label htmlFor="offerPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                    Offer Percentage *
                  </label>
                  <div className="relative max-w-xs">
                    <input 
                      id="offerPercentage" 
                      type="number" 
                      value={offerPercentage} 
                      onChange={handleOfferPercentageChange} 
                      min="0" 
                      max="100" 
                      step="0.01" 
                      className="w-full pr-8 pl-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base" 
                      placeholder="0.00" 
                      required={showOfferPercentage}
                      disabled={isLoading} 
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Discounted prices will be calculated automatically</p>
                </div>
              )}

              {/* Prices */}
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prices by Volume *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {volumes.map((volume) => (
                    <div key={volume} className="flex flex-col">
                      <label htmlFor={`price-${volume}`} className="text-xs sm:text-sm text-gray-600 mb-1">
                        {volume} *
                      </label>
                      <div className="relative">
                        <span className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">$</span>
                        <input
                          id={`price-${volume}`}
                          type="number"
                          value={formData.prices[volume] || ''}
                          onChange={(e) => handlePriceChange(volume, e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full pl-6 sm:pl-8 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                          placeholder="0.00"
                          required
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Discounted Prices (conditionally rendered) */}
              {showDiscountFields && (
                <div className="col-span-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.offerType === 'Offer' ? 'Offer Prices *' : 'Discounted Prices *'}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {volumes.map((volume) => (
                      <div key={`discount-${volume}`} className="flex flex-col">
                        <label htmlFor={`discounted-price-${volume}`} className="text-xs sm:text-sm text-gray-600 mb-1">
                          {volume} *
                        </label>
                        <div className="relative">
                          <span className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">$</span>
                          <input
                            id={`discounted-price-${volume}`}
                            type="number"
                            value={formData.discountedPrices[volume] || ''}
                            onChange={(e) => handleDiscountedPriceChange(volume, e.target.value)}
                            min="0"
                            step="0.01"
                            className="w-full pl-6 sm:pl-8 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                            placeholder="0.00"
                            required={showDiscountFields}
                            disabled={isLoading || formData.offerType === 'Offer'} 
                            readOnly={formData.offerType === 'Offer'}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {formData.offerType === 'Offer' && (
                    <p className="text-xs text-gray-500 mt-1">These prices are automatically calculated based on the offer percentage</p>
                  )}
                </div>
              )}

              {/* Category & Stock */}
              <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white"
                    required
                    disabled={isLoading}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat, index) => (
                      <option key={index} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="stockAvailability" className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Availability *
                  </label>
                  <select
                    id="stockAvailability"
                    name="stockAvailability"
                    value={formData.stockAvailability}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white"
                    required
                    disabled={isLoading}
                  >
                    {stockOptions.map((status, index) => (
                      <option key={index} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fragrance Notes */}
              <div className="col-span-full">
                <label htmlFor="fragranceNoteInput" className="block text-sm font-medium text-gray-700 mb-2">
                  Fragrance Notes
                </label>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <input
                    id="fragranceNoteInput"
                    type="text"
                    value={formData.currentNote}
                    onChange={(e) => setFormData((prev) => ({ ...prev, currentNote: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    placeholder="Add a fragrance note (e.g., Citrus)"
                    disabled={isLoading}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddNote();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddNote}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center justify-center gap-1 text-xs sm:text-sm text-white transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed"
                    disabled={isLoading || !formData.currentNote.trim()}
                    aria-label="Add fragrance note"
                  >
                    <FiPlus size={16} /> Add
                  </button>
                </div>
                {formData.fragranceNotes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.fragranceNotes.map((note, index) => (
                      <div key={index} className="flex items-center bg-gray-100 px-3 py-1 rounded-full text-xs sm:text-sm hover:bg-gray-200 transition-colors">
                        <span>{note}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveNote(index)}
                          className="ml-1.5 text-gray-500 hover:text-red-500 transition-colors disabled:text-gray-300"
                          disabled={isLoading}
                          aria-label={`Remove note ${note}`}
                        >
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Images Section */}
              <div className="col-span-full">
                <label htmlFor="productImages" className="block text-sm font-medium text-gray-700 mb-2">
                  Product Images ({totalImageCount} / {MAX_IMAGES})
                </label>
                <div className="flex flex-col gap-3">
                  {/* File Input */}
                  {canUploadMore && (
                    <input
                      id="productImages"
                      type="file"
                      accept="image/png, image/jpeg, image/webp"
                      multiple
                      onChange={handleImageChange}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isLoading || !canUploadMore}
                      aria-label="Upload product images"
                    />
                  )}
                  {!canUploadMore && <p className="text-xs text-gray-500">Maximum {MAX_IMAGES} images reached.</p>}

                  {/* Image Previews */}
                  {(currentVisibleExistingImages.length > 0 || newImages.length > 0) && (
                    <div className="flex flex-wrap gap-3 mt-2">
                      {/* Existing Images */}
                      {currentVisibleExistingImages.map((url) => (
                        <div key={url} className="relative group">
                          <img
                            src={url}
                            alt="Existing product image"
                            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md border border-gray-300"
                          />
                          <button
                            type="button"
                            onClick={() => markExistingImageForRemoval(url)}
                            className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-75 group-hover:opacity-100 hover:bg-red-700 transition-all disabled:bg-red-300"
                            disabled={isLoading}
                            aria-label="Mark existing image for removal"
                          >
                            <FiTrash2 size={10} strokeWidth={3} />
                          </button>
                        </div>
                      ))}
                      {/* New Images */}
                      {newImages.map((file, index) => (
                        file instanceof File && (
                          <div key={file.name + index} className="relative group">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`New preview ${index + 1}`}
                              className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md border border-blue-300"
                            />
                            <button
                              type="button"
                              onClick={() => removeNewImage(index)}
                              className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-75 group-hover:opacity-100 hover:bg-red-700 transition-all disabled:bg-red-300"
                              disabled={isLoading}
                              aria-label={`Remove new image ${index + 1}`}
                            >
                              <FiX size={12} strokeWidth={3} />
                            </button>
                          </div>
                        )
                      ))}
                    </div>
                  )}

                  {/* Images Marked for Removal */}
                  {imagesToRemove.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-dashed">
                      <p className="text-xs font-medium text-red-600 mb-1">Images marked for removal:</p>
                      <div className="flex flex-wrap gap-2">
                        {imagesToRemove.map((url) => (
                          <div key={'remove-' + url} className="relative group">
                            <img
                              src={url}
                              alt="Marked for removal"
                              className="w-12 h-12 object-cover rounded-md border border-red-300 opacity-50"
                            />
                            <button
                              type="button"
                              onClick={() => unmarkExistingImageForRemoval(url)}
                              className="absolute -top-1 -right-1 bg-gray-600 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-75 group-hover:opacity-100 hover:bg-gray-700 transition-all disabled:bg-gray-300 text-xs"
                              disabled={isLoading}
                              aria-label="Undo removal mark"
                              title="Undo removal"
                            >
                              ↩
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="col-span-full">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                  placeholder="Enter detailed product description..."
                  required
                  disabled={isLoading}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white p-4 sm:p-6 border-t border-gray-200 mt-auto">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 sm:px-6 sm:py-3 border border-gray-300 rounded-lg shadow-sm text-sm sm:text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
              disabled={isLoading}
              aria-label="Cancel and close form"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="update-form-main"
              className="px-4 py-2 sm:px-6 sm:py-3 border border-transparent rounded-lg shadow-sm text-sm sm:text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 flex items-center justify-center min-w-[100px] sm:min-w-[120px]"
              disabled={isLoading}
              aria-label="Update product"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                'Update Product'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateForm;